export class Levenshtein {
    /**
     * Calculate the Levenshtein distance between two strings.
     * The Levenshtein distance is the minimum number of single-character edits
     * (insertions, deletions, or substitutions) required to change one string into another.
     *
     * This implementation uses the Wagner-Fischer algorithm with space optimization.
     *
     * @param s1 - First string
     * @param s2 - Second string
     * @returns The Levenshtein distance between the two strings
     */
    public static distance(s1: string, s2: string): number {
        if (s1 === s2) {
            return 0;
        }

        if (s1.length === 0) {
            return s2.length;
        }

        if (s2.length === 0) {
            return s1.length;
        }

        // If s2 is longer than s1, swap strings to optimize space usage
        if (s1.length > s2.length) {
            const tmp: string = s1;
            s1 = s2;
            s2 = tmp;
        }

        const aLen: number = s1.length;
        const bLen: number = s2.length;

        // Use a single array instead of a matrix for space optimization
        // We only need to keep track of the previous row
        const prevRow: number[] = new Array(aLen + 1);

        // Initialize the first row (distance from empty string)
        for (let i: number = 0; i <= aLen; i++) {
            prevRow[i] = i;
        }

        // Calculate distances row by row
        for (let i: number = 1; i <= bLen; i++) {
            let prevCol: number = i;
            const bChar: string = s2[i - 1];

            for (let j: number = 1; j <= aLen; j++) {
                const aChar: string = s1[j - 1];
                const cost: number = aChar === bChar ? 0 : 1;

                // Calculate minimum of:
                // - deletion: prevRow[j] + 1
                // - insertion: prevCol + 1
                // - substitution: prevRow[j - 1] + cost
                const currentCol: number = Math.min(
                    prevRow[j] + 1,      // deletion
                    prevCol + 1,         // insertion
                    prevRow[j - 1] + cost // substitution
                );

                prevRow[j - 1] = prevCol;
                prevCol = currentCol;
            }

            prevRow[aLen] = prevCol;
        }

        return prevRow[aLen];
    }
}
